// Portal page: /portal/agreements/sign?token=<sign_token>
//
// Public — no portal session required, the URL-embedded token IS the auth.
// Renders the agreement_html the staff sent (snapshotted at send time) in
// a scrollable card, then shows a small "Sign this agreement" panel with:
//   - typed-name input
//   - "I have read, understood, and agree to the terms." checkbox
//   - Sign button (disabled until both are filled)
//
// On success: confirmation panel + auto-redirect to /portal/dashboard
// after 5 seconds.
// On expired/revoked: clear message, no sign controls.

import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PortalShell } from "../components/PortalShell";
import { ApiError, portal, type PortalAgreement } from "../lib/api";
import { SignaturePad } from "../components/SignaturePad";

export default function PortalAgreementSignPage() {
  const [search] = useSearchParams();
  const token = (search.get("token") || "").trim();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agreement, setAgreement] = useState<PortalAgreement | null>(null);

  const [typedName, setTypedName] = useState("");
  const [sigPng, setSigPng] = useState<string | null>(null);
  const [agreementToken, setAgreementToken] = useState<string | null>(null);
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [signedAt, setSignedAt] = useState<string | null>(null);

  // Initial load.
  useEffect(() => {
    if (!token) {
      setError("This link is missing a sign token. Ask staff to send a new agreement.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    // New token = possibly a different client: hide the old agreement and wipe signing state.
    setAgreement(null); setAgreementToken(null); setLoading(true); setAgree(false); setTypedName(""); setSigPng(null); setSubmitErr(null);
    (async () => {
      try {
        const a = await portal.getAgreement(token);
        if (cancelled) return;
        setAgreement(a); setAgreementToken(token);
      } catch (e: any) {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 404) {
          setError("This sign link is not valid. It may have been revoked or never existed.");
        } else {
          setError(e?.body?.message || e?.message || "Could not load the agreement.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Auto-redirect 5s after a successful sign.
  useEffect(() => {
    if (!signedAt) return;
    const t = setTimeout(() => {
      window.location.replace("/portal/dashboard");
    }, 5000);
    return () => clearTimeout(t);
  }, [signedAt]);

  const onSubmit = useCallback(async () => {
    setSubmitErr(null);
    if (!token) return;
    if (!agreement || agreementToken !== token) { setSubmitErr("Still loading the agreement — try again in a moment."); return; }
    if (typedName.trim().length < 2) {
      setSubmitErr("Type your full legal name to sign.");
      return;
    }
    if (!agree) {
      setSubmitErr("Please check the agreement box to sign.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await portal.signAgreement(token, typedName.trim(), true, sigPng);
      setSignedAt(r.signed_at);
    } catch (e: any) {
      const code = e instanceof ApiError ? (e.body?.error || e.message) : (e?.message || "error");
      if (code === "expired") setSubmitErr("This sign link has expired. Ask staff for a new one.");
      else if (code === "revoked") setSubmitErr("This agreement was revoked. Ask staff for a new one.");
      else if (code === "already_signed") setSubmitErr("This agreement has already been signed.");
      else if (code === "invalid_name") setSubmitErr("Type your full legal name to sign.");
      else if (code === "agree_required") setSubmitErr("Please check the agreement box to sign.");
      else setSubmitErr(e?.body?.message || e?.message || "Could not sign the agreement.");
    } finally {
      setSubmitting(false);
    }
  }, [token, typedName, agree, sigPng, agreement, agreementToken]);

  if (loading) {
    return (
      <PortalShell>
        <div className="portal-card" role="status" aria-live="polite">
          <p className="portal-sub on-card">Loading your service agreement…</p>
        </div>
      </PortalShell>
    );
  }

  if (error) {
    return (
      <PortalShell>
        <div className="portal-card" role="alert">
          <h1 className="portal-headline on-card" style={{ fontSize: 24 }}>This sign link can't be used.</h1>
          <p className="portal-sub on-card">{error}</p>
        </div>
      </PortalShell>
    );
  }

  if (!agreement) {
    return (
      <PortalShell>
        <div className="portal-card" role="alert">
          <p className="portal-sub on-card">Agreement not found.</p>
        </div>
      </PortalShell>
    );
  }

  const expired = agreement.status === "expired";
  const revoked = agreement.status === "revoked";
  const alreadySigned = agreement.status === "signed" && !signedAt;
  const canSign = agreement.status === "pending" && !signedAt;

  const headerName = agreement.client_name || "Client";

  return (
    <PortalShell>
      <section className="portal-section">
        <p className="portal-eyebrow">Service Agreement</p>
        <h1 className="portal-headline">{headerName}, review &amp; sign your agreement.</h1>
        <p className="portal-sub">
          Below is the full service agreement we&rsquo;ll be working under, including the federal
          Consumer Credit File Rights notice and your 3-day right to cancel. Read it through,
          then type your name and check the agreement box to sign.
        </p>
      </section>

      {signedAt && (
        <div
          className="portal-card"
          data-testid="agreement-signed-confirmation"
          role="status"
          aria-live="polite"
          style={{ background: "var(--ne-success-bg)", borderColor: "var(--ne-success-border)" }}
        >
          <h2 className="portal-headline on-card" style={{ fontSize: 22, color: "var(--ne-success-text)" }}>
            Thank you, {typedName.trim() || agreement.client_name}.
          </h2>
          <p className="portal-sub on-card">
            Your agreement has been signed successfully. Your signed copy will be emailed to you
            shortly. Redirecting you to your portal dashboard in 5 seconds.
          </p>
        </div>
      )}

      {expired && (
        <div className="portal-card" data-testid="agreement-expired">
          <h2 className="portal-headline on-card" style={{ fontSize: 22 }}>This sign link has expired.</h2>
          <p className="portal-sub on-card">
            Reach out to our team and we&rsquo;ll send you a fresh agreement.
          </p>
        </div>
      )}

      {revoked && (
        <div className="portal-card" data-testid="agreement-revoked">
          <h2 className="portal-headline on-card" style={{ fontSize: 22 }}>This agreement was revoked.</h2>
          <p className="portal-sub on-card">
            Reach out to our team and we&rsquo;ll send you a fresh agreement.
          </p>
        </div>
      )}

      {alreadySigned && (
        <div className="portal-card" data-testid="agreement-already-signed">
          <h2 className="portal-headline on-card" style={{ fontSize: 22 }}>This agreement is already signed.</h2>
          <p className="portal-sub on-card">
            Signed by <strong>{agreement.signed_by_name || "—"}</strong> on{" "}
            <strong>{agreement.signed_at ? new Date(agreement.signed_at).toLocaleString() : "—"}</strong>.
          </p>
        </div>
      )}

      {/* The agreement body. Renders the snapshot HTML the server stored at
          send-time, so we display exactly what the signer is agreeing to. */}
      <div
        className="portal-card"
        data-testid="agreement-body"
        role="region"
        aria-label="Service agreement full text"
        tabIndex={0}
        style={{
          maxHeight: "70vh",
          overflowY: "auto",
          background: "#fff",
          color: "#000",
          padding: 24,
          fontFamily: "Georgia, 'Times New Roman', serif",
        }}
        // Snapshot HTML is server-rendered and stored as agreement_body_snapshot;
        // the server escapes all client-supplied substrings before snapshotting.
        dangerouslySetInnerHTML={{ __html: agreement.agreement_html }}
      />

      {canSign && (
        <section
          className="portal-card"
          data-testid="agreement-sign-panel"
          aria-labelledby="agreement-sign-heading"
          aria-describedby="agreement-sign-instructions agreement-attestation-text"
          style={{ marginTop: 16 }}
        >
          <h2
            id="agreement-sign-heading"
            className="portal-headline on-card"
            style={{ fontSize: 22 }}
          >
            Sign this agreement
          </h2>
          <p id="agreement-sign-instructions" className="portal-sub on-card">
            Type your full legal name and check the agreement box. By signing, you consent under
            the federal E-SIGN Act to use of an electronic signature. Your typed name will serve
            as your legally binding signature on this Credit Services Agreement.
          </p>

          <label
            htmlFor="agreement-typed-name-input"
            style={{ display: "block", marginTop: 12, fontSize: 14, fontWeight: 600 }}
          >
            Full legal name
            <input
              id="agreement-typed-name-input"
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value.slice(0, 120))}
              disabled={submitting}
              data-testid="agreement-typed-name"
              autoComplete="name"
              required
              aria-required="true"
              aria-describedby="agreement-typed-name-help"
              style={{
                display: "block",
                width: "100%",
                marginTop: 6,
                padding: "10px 12px",
                fontSize: 16,
                fontWeight: 400,
                border: "1px solid #bbb",
                borderRadius: 4,
              }}
            />
          </label>

          <SignaturePad
            key={token}
            onChange={setSigPng}
            disabled={submitting}
            label="Draw your signature (optional)"
            hint="Sign here — this exact signature prints on every dispute letter we send for you"
          />
          <p
            id="agreement-typed-name-help"
            className="portal-sub on-card"
            style={{ marginTop: 6, fontSize: 13 }}
          >
            Enter your name exactly as you would sign a legal document.
          </p>

          <div
            style={{
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
              marginTop: 14,
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            <input
              id="agreement-agree-checkbox-input"
              type="checkbox"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              disabled={submitting}
              data-testid="agreement-agree-checkbox"
              required
              aria-required="true"
              aria-describedby="agreement-attestation-text"
              style={{ marginTop: 4 }}
            />
            <label
              htmlFor="agreement-agree-checkbox-input"
              id="agreement-attestation-text"
            >
              I attest that I have read, understood, and agree to the terms of this Credit
              Services Agreement, including the Consumer Credit File Rights notice and my
              3-day right to cancel. I understand that checking this box and typing my name
              above constitutes my legally binding electronic signature.
            </label>
          </div>

          {submitErr && (
            <p
              className="portal-error"
              data-testid="agreement-sign-error"
              role="alert"
              aria-live="assertive"
              style={{ marginTop: 12 }}
            >
              {submitErr}
            </p>
          )}

          <button
            type="button"
            className="portal-btn-primary wide"
            onClick={onSubmit}
            disabled={submitting || typedName.trim().length < 2 || !agree}
            data-testid="agreement-sign-submit"
            aria-busy={submitting}
            style={{ marginTop: 16 }}
          >
            {submitting ? "Signing…" : "Sign agreement"}
          </button>
        </section>
      )}
    </PortalShell>
  );
}
